/**
 * AURA Internationalization (i18n)
 * Translation layer for all user-facing strings in the widget
 */

import { Logger } from './logger';
import { TRANSLATIONS } from './translations';

const logger = new Logger('i18n');

export interface TranslationSet {
  panelTitle: string;
  close: string;
  resetSettings: string;
  statement: string;
  hideInterface: string;
  searchPlaceholder: string;
  tabProfiles: string;
  tabContent: string;
  tabColors: string;
  tabOrientation: string;
  profilesHeading: string;
  seizureSafe: string;
  seizureSafeDesc: string;
  visionImpaired: string;
  visionImpairedDesc: string;
  adhdFriendly: string;
  adhdFriendlyDesc: string;
  cognitiveDisability: string;
  cognitiveDisabilityDesc: string;
  keyboardNavigation: string;
  keyboardNavigationDesc: string;
  screenReader: string;
  screenReaderDesc: string;
  contentAdjustments: string;
  contentScaling: string;
  fontSizing: string;
  lineHeight: string;
  letterSpacing: string;
  readableFont: string;
  highlightTitles: string;
  highlightLinks: string;
  textMagnifier: string;
  colorAdjustments: string;
  contrast: string;
  darkContrast: string;
  lightContrast: string;
  highContrast: string;
  saturation: string;
  highSaturation: string;
  lowSaturation: string;
  monochrome: string;
  customColors: string;
  textColor: string;
  titleColor: string;
  backgroundColor: string;
  orientationAdjustments: string;
  mediaAndMotion: string;
  muteSounds: string;
  hideImages: string;
  stopAnimations: string;
  readingAids: string;
  readMode: string;
  readingGuide: string;
  readingMask: string;
  focusAndNavigation: string;
  highlightHover: string;
  highlightFocus: string;
  cursor: string;
  bigBlackCursor: string;
  bigWhiteCursor: string;
  accessibilityStatement: string;
  poweredBy: string;
  learnMore: string;
  statementTitle: string;
  statementClose: string;
  on: string;
  off: string;
}

export interface LanguageOption {
  code: string;
  label: string;
  flag: string;
}

export const SUPPORTED_LANGUAGES: LanguageOption[] = [
  { code: 'en', label: 'English (US)', flag: '🇺🇸' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'pt', label: 'Português', flag: '🇧🇷' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'zh', label: '中文', flag: '🇨🇳' },
  { code: 'ja', label: '日本語', flag: '🇯🇵' },
  { code: 'ko', label: '한국어', flag: '🇰🇷' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦' },
];

let currentLanguage = 'en';
type LanguageChangeCallback = (lang: string) => void;
const changeListeners: Set<LanguageChangeCallback> = new Set();

export function setLanguage(lang: string): void {
  const resolvedLang = TRANSLATIONS[lang] ? lang : 'en';
  if (resolvedLang === currentLanguage) return;

  currentLanguage = resolvedLang;
  logger.info(`Language changed to: ${resolvedLang}`);
  changeListeners.forEach((cb) => cb(resolvedLang));
}

export function getLanguage(): string {
  return currentLanguage;
}

export function t(key: keyof TranslationSet): string {
  const set = TRANSLATIONS[currentLanguage] ?? TRANSLATIONS['en']!;
  return set![key] ?? TRANSLATIONS['en']![key] ?? key;
}

export function onLanguageChange(callback: LanguageChangeCallback): () => void {
  changeListeners.add(callback);
  return () => changeListeners.delete(callback);
}
